import DatePicker from "@/components/DatePicker";
import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import Form from "@/components/Form";
import SubmitButton from "@/components/SubmitButton";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ContractorSelect from "@/components/ContractorSelect";
import { writeDepartmentBasedAssignments } from "@/schemas/departmentBasedAssignments";
import { writeRoleBasedAssignments } from "@/schemas/roleBasedAssignments";
import { z } from "@/zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAddNewEmptyContractorAssignmentMutation } from "../hooks/useAddNewEmptyContractorAssignmentMutation";
import {
  createEmptyDepartmentBasedAssignments,
  createEmptyRoleBasedAssignments,
} from "../utils";
import { useContractorsAssignmentsContext } from "./ContractorsAssignmentsContext";
import { isRequired } from "@/lib/form";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { ContractorsAssignmentsDepartmentBasedFields } from "./ContractorsAssignmentsDepartmentBasedFields";
import { ContractorsAssignmentsProjectRoleBasedFields } from "./ContractorsAssignmentsProjectRoleBasedFields";
import { ContractorsAssignmentsRoleBasedFields } from "./ContractorsAssignmentsRoleBasedFields";

export const ContractorsAssignmentsAddDialog = () => {
  const { t } = useTranslation();
  const {
    isRoleBasedProject,
    isAddContractorDialogOpen,
    setAddContractorDialogOpen,
  } = useContractorsAssignmentsContext();

  const projectId = useProjectId();
  const [selectedProjectId, setSelectedProjectId] = useState<
    string | undefined
  >();

  const schema = useMemo(
    () =>
      isRoleBasedProject
        ? writeRoleBasedAssignments
        : writeDepartmentBasedAssignments,
    [isRoleBasedProject],
  );

  type schemaType = z.infer<typeof schema>;

  const defaultValues = useMemo(() => {
    const assignment = isRoleBasedProject
      ? createEmptyRoleBasedAssignments()
      : createEmptyDepartmentBasedAssignments();
    return assignment as schemaType;
  }, [isRoleBasedProject]);

  const form = useForm<schemaType>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const closeDialog = useCallback(() => {
    setSelectedProjectId(undefined);
    form.reset(defaultValues);
    setAddContractorDialogOpen(false);
  }, [form, defaultValues, setAddContractorDialogOpen]);

  const handleProjectChangeWithReset = useCallback(
    (newProjectId: string) => {
      setSelectedProjectId(newProjectId);
      form.setValue("project_role_id", "");
      form.setValue("store_id", "");
    },
    [form],
  );

  const mutation = useAddNewEmptyContractorAssignmentMutation(closeDialog);

  return (
    <Dialog
      open={isAddContractorDialogOpen}
      onOpenChange={(open) => !open && closeDialog()}
    >
      <DialogContent>
        <Form
          form={form}
          handleSubmit={mutation.mutateAsync}
          className="min-w-0"
        >
          <DialogHeader>
            <DialogTitle>
              {t("contractorsAssignments.addDialog.title")}
            </DialogTitle>
          </DialogHeader>
          <Fieldset>
            <Field
              name="start_date"
              label={t("contractorsAssignments.fields.start_date")}
              required={isRequired(schema.shape.start_date)}
              render={({ field }) => (
                <DatePicker {...field} className="w-40" inDialog />
              )}
            />
            <Field
              name="end_date"
              label={t("contractorsAssignments.fields.end_date")}
              required={isRequired(schema.shape.end_date)}
              render={({ field }) => (
                <DatePicker {...field} className="w-40" inDialog />
              )}
            />
            <Field
              name="contractor_id"
              label={t("contractorsAssignments.fields.contractor_id")}
              required={isRequired(schema.shape.contractor_id)}
              render={({ field }) => (
                <ContractorSelect {...field} className="w-80" inDialog />
              )}
            />
            {!isRoleBasedProject && (
              <ContractorsAssignmentsDepartmentBasedFields />
            )}
            {isRoleBasedProject && projectId && (
              <ContractorsAssignmentsProjectRoleBasedFields
                projectId={projectId}
              />
            )}
            {isRoleBasedProject && !projectId && (
              <ContractorsAssignmentsRoleBasedFields
                selectedProjectId={selectedProjectId}
                onProjectChangeWithReset={handleProjectChangeWithReset}
              />
            )}
          </Fieldset>
          <DialogFooter className="gap-3">
            <DialogClose asChild>
              <Button variant="link">
                {t("contractorsAssignments.addDialog.cancel")}
              </Button>
            </DialogClose>
            <SubmitButton />
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
